import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    AuthService,
    EmployeesClient,
    EmploymentDetailsDto,
    PermissionType,
    PositionStatus,
    UpdateEmploymentDetailsCommand
} from '@services';
import { DxLoadPanelModule, DxSelectBoxModule } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { EmploymentDetailRowComponent } from '../employment-detail-row/employment-detail-row.component';
import { PositionStatusPipe } from 'src/app/pipes/position-status.pipe';
import { YES_NO_OPTIONS } from 'src/app/shared/constants';

@Component({
    selector: 'app-employment-details',
    templateUrl: './employment-details.component.html',
    standalone: true,
    imports: [CommonModule, DxLoadPanelModule, DxSelectBoxModule, EmploymentDetailRowComponent, PositionStatusPipe]
})
export class EmploymentDetailsComponent implements OnInit {
    private employeesClient = inject(EmployeesClient);
    protected authService = inject(AuthService);

    @Input() userId: string;
    @Input() forceReadOnly = false;
    @Output() isDataChanged = new EventEmitter<boolean>();
    @Output() systemAccessChanged = new EventEmitter<boolean>();

    employmentData: EmploymentDetailsDto;
    isLoading = false;

    protected readonly yesNoOptions = YES_NO_OPTIONS;

    ngOnInit() {
        this.loadData();
    }

    loadData() {
        if (!this.userId) return;
        this.isLoading = true;
        this.employeesClient.getEmploymentDetails(this.userId).subscribe({
            next: (data) => {
                this.employmentData = data;
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
            }
        });
    }

    public save(): Observable<void> {
        if (this.isReadOnly || !this.employmentData) return of(void 0);

        const command = new UpdateEmploymentDetailsCommand({
            userId: this.userId,
            sapAccess: this.employmentData.sapAccess,
            dwaAccess: this.employmentData.dwaAccess,
            nextViewAccess: this.employmentData.nextViewAccess
        });

        return this.employeesClient.updateEmploymentDetails(command).pipe(
            catchError((err) => {
                const errors = err?.response?.errors || [{ message: 'Failed to update employment details' }];
                errors.forEach((errMsg: any) => {
                    notify({
                        message: errMsg.message || errMsg,
                        type: "error",
                        displayTime: 5000,
                    }, {
                        position: "bottom right",
                        direction: "up-push"
                    });
                });
                return throwError(() => err);
            })
        );
    }

    onFieldChange(e: { event?: Event }) {
        // A reload after a profile action changes the values programmatically; that is not an edit.
        if (!e.event) return;

        this.isDataChanged.emit(true);
        const { sapAccess, dwaAccess, nextViewAccess } = this.employmentData;
        this.systemAccessChanged.emit(sapAccess === true || dwaAccess === true || nextViewAccess === true);
    }

    protected getStatusBadgeClass(status: PositionStatus): string {
        switch (status) {
            case PositionStatus.Terminated:
            case PositionStatus.Deactivated: return 'badge-danger';
            case PositionStatus.Inactive: return 'badge-warning';
            case PositionStatus.Leave: return 'badge-secondary';
            default: return 'badge-success';
        }
    }

    get isReadOnly(): boolean {
        return this.forceReadOnly || !!this.employmentData?.systemAccessLocked || !this.authService.canEditProfileSection(this.userId, PermissionType.EmployeeEmploymentDetailsOwn, PermissionType.EmployeeEmploymentDetailsOthers);
    }
}
